// GENERATED BY portal-gen — DO NOT EDIT.
//
// Source: Identity & Team Service — identity.v1.yaml
// Regenerate with `npm run generate`; `npm run check` byte-diffs this file against a
// fresh run and fails the build on any difference.
//
// Every object below preserves the contract's `additionalProperties: false` as `.strict()`.
// That is a privacy control, not a typing convenience: a control-plane response that ever
// grows a content-bearing field must fail to parse, not render.

import { z } from "zod";

export const Role = z.enum(["admin", "member"]);
export type Role = z.infer<typeof Role>;

export const Invite = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  email: z.string().email(),
  role: Role,
  status: z.enum(["pending", "accepted", "revoked", "expired"]),
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
}).strict();
export type Invite = z.infer<typeof Invite>;

export const MembershipStatus = z.enum(["active", "invited", "suspended"]);
export type MembershipStatus = z.infer<typeof MembershipStatus>;

export const Plan = z.enum(["individual", "team", "enterprise"]);
export type Plan = z.infer<typeof Plan>;

export const MembershipSummary = z.object({
  orgId: z.string().uuid(),
  orgName: z.string(),
  role: Role,
  status: MembershipStatus,
  plan: Plan,
}).strict();
export type MembershipSummary = z.infer<typeof MembershipSummary>;

export const Me = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  emailVerified: z.boolean(),
  displayName: z.string().nullable().optional(),
  primaryOrgId: z.string().uuid().nullable().optional(),
  memberships: z.array(MembershipSummary),
}).strict();
export type Me = z.infer<typeof Me>;

export const Member = z.object({
  userId: z.string().uuid(),
  email: z.string().email().nullable().optional(),
  displayName: z.string().nullable().optional(),
  role: Role,
  status: MembershipStatus,
  joinedAt: z.string().datetime().nullable().optional(),
}).strict();
export type Member = z.infer<typeof Member>;

export const OrgStatus = z.enum(["trial", "active", "past_due", "canceled"]);
export type OrgStatus = z.infer<typeof OrgStatus>;

export const Org = z.object({
  id: z.string().uuid(),
  name: z.string(),
  plan: Plan,
  status: OrgStatus,
  trialEndsAt: z.string().datetime().nullable().optional(),
  seatCount: z.number().int(),
  seatLimit: z.number().int(),
  createdAt: z.string().datetime(),
}).strict();
export type Org = z.infer<typeof Org>;

export const ProvisionAccountRequest = z.object({
  intent: z.enum(["personal", "none"]).optional(),  // default: "personal"
  displayName: z.string().max(128).optional(),
}).strict();
export type ProvisionAccountRequest = z.infer<typeof ProvisionAccountRequest>;

// ---- Operations ----
//
// `roleGate` is documentation carried into the type system, NOT enforcement. It lets a
// lint test assert that every admin-gated call site sits inside a RequireAdmin subtree,
// and it lets the client attach the right context to a 403.

/** Provision (or return) the caller's profile after GCIP sign-in. Idempotent on the GCIP uid. */
export const provisionAccount = {
  id: "provisionAccount",
  method: "POST",
  path: "/v1/identity/accounts",
  roleGate: "any",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: ProvisionAccountRequest,
  successStatus: 200,
  response: Me,
  errors: [401, 409, 503] as const,
} as const;
export type ProvisionAccountResponse = z.infer<typeof provisionAccount.response>;

/** The caller's profile and org memberships (scoped to the caller's sub). */
export const getMe = {
  id: "getMe",
  method: "GET",
  path: "/v1/identity/me",
  roleGate: "any",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: Me,
  errors: [401] as const,
} as const;
export type GetMeResponse = z.infer<typeof getMe.response>;

/** Update the caller's own profile (display name only). */
export const updateMe = {
  id: "updateMe",
  method: "PATCH",
  path: "/v1/identity/me",
  roleGate: "any",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: z.object({
  displayName: z.string().min(1).max(128).optional(),
}).strict(),
  successStatus: 200,
  response: Me,
  errors: [400, 401] as const,
} as const;
export type UpdateMeRequest = z.infer<typeof updateMe.request>;
export type UpdateMeResponse = z.infer<typeof updateMe.response>;

/** Ask the IdP to (re)send the caller's email-verification link. */
export const resendVerifyEmail = {
  id: "resendVerifyEmail",
  method: "POST",
  path: "/v1/identity/account/verify-email/resend",
  roleGate: "any",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 202,
  response: null,
  errors: [401, 429, 503] as const,
} as const;

/** Ask the IdP to send a password-reset link for a given email. */
export const requestPasswordReset = {
  id: "requestPasswordReset",
  method: "POST",
  path: "/v1/identity/account/password-reset",
  roleGate: "any",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: z.object({
  email: z.string().email(),
}).strict(),
  successStatus: 202,
  response: null,
  errors: [400, 429, 503] as const,
} as const;
export type RequestPasswordResetRequest = z.infer<typeof requestPasswordReset.request>;

/** Create a team org; the caller becomes its first admin. */
export const createOrg = {
  id: "createOrg",
  method: "POST",
  path: "/v1/identity/orgs",
  roleGate: "any",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: z.object({
  name: z.string().min(1).max(128),
}).strict(),
  successStatus: 201,
  response: Org,
  errors: [401, 503] as const,
} as const;
export type CreateOrgRequest = z.infer<typeof createOrg.request>;
export type CreateOrgResponse = z.infer<typeof createOrg.response>;

/** Get an org the caller belongs to. */
export const getOrg = {
  id: "getOrg",
  method: "GET",
  path: "/v1/identity/orgs/{orgId}",
  roleGate: "any",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: Org,
  errors: [401, 403, 404] as const,
} as const;
export type GetOrgResponse = z.infer<typeof getOrg.response>;

/** List members of an org (any member may read; emails visible to admins only). */
export const listMembers = {
  id: "listMembers",
  method: "GET",
  path: "/v1/identity/orgs/{orgId}/members",
  roleGate: "any",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: z.object({
  members: z.array(Member),
  seatCount: z.number().int(),
  seatLimit: z.number().int(),
}).strict(),
  errors: [401, 403] as const,
} as const;
export type ListMembersResponse = z.infer<typeof listMembers.response>;

/** Change a member's role (admin only). Cannot demote the last admin. */
export const updateMemberRole = {
  id: "updateMemberRole",
  method: "PATCH",
  path: "/v1/identity/orgs/{orgId}/members/{userId}",
  roleGate: "admin",
  pathParams: ["orgId", "userId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: z.object({
  role: Role,
}).strict(),
  successStatus: 200,
  response: Member,
  errors: [400, 401, 403, 409] as const,
} as const;
export type UpdateMemberRoleRequest = z.infer<typeof updateMemberRole.request>;
export type UpdateMemberRoleResponse = z.infer<typeof updateMemberRole.response>;

/** Remove a member (admin only). Frees a seat and emits seat.changed. */
export const removeMember = {
  id: "removeMember",
  method: "DELETE",
  path: "/v1/identity/orgs/{orgId}/members/{userId}",
  roleGate: "admin",
  pathParams: ["orgId", "userId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 204,
  response: null,
  errors: [401, 403, 409] as const,
} as const;

/** List pending invites for an org (admin only). */
export const listInvites = {
  id: "listInvites",
  method: "GET",
  path: "/v1/identity/orgs/{orgId}/invites",
  roleGate: "admin",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: z.array(Invite),
  errors: [401, 403] as const,
} as const;
export type ListInvitesResponse = z.infer<typeof listInvites.response>;

/** Invite a member by email (admin only). Enforces the org seat limit. */
export const createInvite = {
  id: "createInvite",
  method: "POST",
  path: "/v1/identity/orgs/{orgId}/invites",
  roleGate: "admin",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: z.object({
  email: z.string().email(),
  role: Role.optional(),  // default: "member"
}).strict(),
  successStatus: 201,
  response: Invite,
  errors: [401, 403, 409] as const,
} as const;
export type CreateInviteRequest = z.infer<typeof createInvite.request>;
export type CreateInviteResponse = z.infer<typeof createInvite.response>;

/** Revoke a pending invite (admin only). */
export const revokeInvite = {
  id: "revokeInvite",
  method: "DELETE",
  path: "/v1/identity/orgs/{orgId}/invites/{inviteId}",
  roleGate: "admin",
  pathParams: ["orgId", "inviteId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 204,
  response: null,
  errors: [401, 403, 404] as const,
} as const;

/** Accept an invite with its token; the authenticated caller joins the org. */
export const acceptInvite = {
  id: "acceptInvite",
  method: "POST",
  path: "/v1/identity/invites/accept",
  roleGate: "any",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: z.object({
  token: z.string(),
}).strict(),
  successStatus: 200,
  response: Member,
  errors: [400, 401, 409, 429] as const,
} as const;
export type AcceptInviteRequest = z.infer<typeof acceptInvite.request>;
export type AcceptInviteResponse = z.infer<typeof acceptInvite.response>;

export const identityOperations = { provisionAccount, getMe, updateMe, resendVerifyEmail, requestPasswordReset, createOrg, getOrg, listMembers, updateMemberRole, removeMember, listInvites, createInvite, revokeInvite, acceptInvite } as const;
